'use client';

import { useState, Fragment } from 'react';
import moment from 'moment';
import { useSuspenseQuery, useApolloClient } from '@apollo/client/react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import DatedEventList from '@/components/dated-event-list';
import RecentEventList from '@/components/recent-event-list';
import CalendarEmptyState from '@/components/calendar/calendar-empty-state';
import SectionHeader from '@/components/section-header';
import { cityEventsQuery, cityEventsPaginationQuery } from '@/apollo/cities';
import LoadMore from '@/components/load-more';
import { filterableRoute } from '@/utils/routes';
import type { CalendarFilters } from '@/components/calendar/filter-bar';
import MaterialLink from '@/components/material-link';

const limit = 30;

export default function CalendarEventList({ cityId, filters, urlStart }: { cityId: string; filters: CalendarFilters; urlStart: string | null }) {
  const client = useApolloClient();

  // Reads from PreloadQuery cache on SSR, auto-fetches on filter change
  const { data } = useSuspenseQuery(cityEventsQuery, {
    variables: { id: cityId, filters, limit },
  });
  const city = data.city;

  // Track additional "Load More" events separately.
  // These reset automatically when filters change because the parent
  // re-keys this component, causing a fresh mount.
  const [additionalEvents, setAdditionalEvents] = useState([]);
  const [hasMore, setHasMore] = useState(city.events.pageInfo.hasNextPage);
  const [cursor, setCursor] = useState<string | null>(city.events.pageInfo.endCursor ?? null);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);

  const isPastStart = urlStart && moment(urlStart, 'YYYY-MM-DD').isBefore(moment(), 'day');
  const allEvents = [...city.events.nodes, ...additionalEvents];

  const emptyStateFilters = ['rsvp', 'managed', 'drafts'] as const;
  if (allEvents.length === 0 && emptyStateFilters.includes(filters.filter as typeof emptyStateFilters[number])) {
    return <CalendarEmptyState filter={filters.filter as 'rsvp' | 'managed' | 'drafts'} />;
  }

  function handleLoadMore() {
    if (!hasMore) return;
    setLoadMoreLoading(true);
    client.query({
      query: cityEventsPaginationQuery,
      variables: { id: cityId, filters, limit, cursor, offset: allEvents.length },
    }).then(({ data: paginationData }) => {
      setAdditionalEvents(prev => [...prev, ...paginationData.city.events.nodes]);
      setHasMore(paginationData.city.events.pageInfo.hasNextPage);
      setCursor(paginationData.city.events.pageInfo.endCursor ?? null);
      setLoadMoreLoading(false);
    }).catch(() => {
      alert('Failed to load more events');
      setLoadMoreLoading(false);
    });
  }

  return (
    <Fragment>
      {urlStart && (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
          Showing events starting from {moment(urlStart, 'YYYY-MM-DD').format('MMMM D, YYYY')}.{' '}
          <MaterialLink href={filterableRoute('/calendar', { filter: filters.filter, type: filters.type, categoryIds: filters.categoryIds })}>
            Jump to today
          </MaterialLink>
        </Typography>
      )}
      {filters.filter === 'recent' ? (
        <Container maxWidth="md" disableGutters>
          <SectionHeader sx={{ mt: 0 }}>Recently Added</SectionHeader>
          <RecentEventList
            events={allEvents}
            emptyMessage="No recently added events"
          />
        </Container>
      ) : (
        <DatedEventList
          events={allEvents}
          timezone={city.timezone}
          allowPast={filters.filter === 'past'}
          showPastDates={!!isPastStart}
        />
      )}

      {hasMore && (
        <Container maxWidth="md" disableGutters>
          <LoadMore
            onClick={handleLoadMore}
            loading={loadMoreLoading}
          />
        </Container>
      )}
    </Fragment>
  );
}
